// @flow
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormValidator } from '../../../../../utils/form/index';
import { defineMessages, intlShape } from 'react-intl';
import { FBLogin } from '../../../../../utils/facebook/index';
import { getBrowserLanguage } from '../../../../local-values/utils/countries';
import Checkbox from '../../../../../elements/Checkbox/Checkbox';

import style from '../../authForm.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import formStyle from '../../../../../assets/styles/form.scss';
import facebookLogo from '../../../../../assets/img/social/facebook.png';
import checkboxStyle from '../../../../../elements/Checkbox/Checkbox.scss';

import type { FormValidationResult } from '../../../../../utils/form/index';
import type { HTTPError } from '../../../../../error/index';
import type { Language } from '../../../../../entities/index';
import type { FBLoginResponse } from '../../../../../utils/facebook/index';
import type { LocaleState } from '../../../../locale/duck';

type Props = {
  +fbLogin: (country: ?string, language: Language, token: string,
             email?: string, redirectTo?: string) => void;
  +register: (country: ?string, firstName: string, lastName: string,
              email: string, password: string, language: string) => void;
  +isRegistering: ?boolean;
  +isAuthing: ?boolean;
  +signUpError: ?HTTPError;
  +authenticated: ?boolean;
  +redirectTo?: string;
  +handleClickLogin?: (e: Event) => void;
  +authModalVisible?: boolean;
  +authModalShowLogin?: boolean;
  +handleClose?: () => void;
  +locale: LocaleState;
  +registerSuccessState?: boolean;
};

type State = {
  name: {
    value: string;
    errors?: string[];
  };
  lastName: {
    value: string;
    errors?: string[];
  };
  email: {
    value: string;
    errors?: string[];
  };
  password: {
    value: string;
    errors?: string[];
  };
  repeatPassword: {
    value: string;
    errors?: string[];
  };
  acceptTerms: boolean;
};

const messages = defineMessages({
  namePlaceholder: {
    id: 'signup.name.placeholder',
  },
  lastNamePlaceholder: {
    id: 'signup.lastName.placeholder',
  },
  emailPlaceholder: {
    id: 'auth.email.placeholder',
  },
  passwordPlaceholder: {
    id: 'auth.password.placeholder',
  },
  repeatPasswordPlaceholder: {
    id: 'signup.repeatPassword.placeholder',
  },
});

const initialSignUpState = {
  name: {
    value: '',
  },
  lastName: {
    value: '',
  },
  email: {
    value: '',
  },
  password: {
    value: '',
  },
  repeatPassword: {
    value: '',
  },
  acceptTerms: true,
};

export default class SignUpForm extends Component<Props, State> {
  formValidator: FormValidator;
  state = { ...initialSignUpState };

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      name: {
        required: 'required',
      },
      lastName: {
        required: 'required',
      },
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      },
      password: {
        required: 'required',
        min: (value: string) => value.length < 8,
      },
      repeatPassword: {
        required: (values: string[]) => !values[0],
        notSame: (values: string[]) => {
          return values[0] !== values[1];
        }
      }
    });
  }

  componentDidUpdate(prevProps: Props) {
    if ((this.props.authModalVisible !== prevProps.authModalVisible ||
      this.props.authModalShowLogin !== prevProps.authModalShowLogin) &&
      this.props.authModalVisible && !this.props.authModalShowLogin) {
      this.setState(initialSignUpState);
    }
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleBlur(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const v = name === 'repeatPassword' ? [value, this.state.password.value] : value;
      const errors = this.formValidator.validateField(name, v);
      if (errors.length && (errors[0] === 'emailFormat' || errors[0] === 'notSame' || errors[0] === 'min')) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState({ [name]: { value } });
      }
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { register, locale } = this.props;
    const nameValue = this.state.name.value;
    const lastNameValue = this.state.lastName.value;
    const emailValue = this.state.email.value;
    const passwordValue = this.state.password.value;
    const repeatPasswordValue = this.state.repeatPassword.value;
    const errors = this.formValidator.validateForm({
      name: nameValue,
      lastName: lastNameValue,
      email: emailValue,
      password: passwordValue,
      repeatPassword: [repeatPasswordValue, passwordValue],
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors } }));
    } else {
      const localeUpper = locale.locale ? locale.locale.toUpperCase() : '';
      const lang = localeUpper || getBrowserLanguage();
      register(locale.country, nameValue, lastNameValue, emailValue, passwordValue, lang);
    }
  }

  handleLoginFB(e: Event) {
    const { fbLogin, redirectTo, locale } = this.props;
    FBLogin(window).then((response: FBLoginResponse) => {
      if (response.status === 'connected' && response.authResponse) {
        const localeUpper = locale.locale ? locale.locale.toUpperCase() : '';
        const lang = localeUpper || getBrowserLanguage();
        fbLogin(locale.country, lang, response.authResponse.accessToken, undefined, redirectTo);
      }
    });
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.signUpError) {
      if (nextProps.signUpError.code === 400 &&
        nextProps.signUpError.name === 'DuplicateUserError') {
        this.setState({
          email: {
            ...this.state.email,
            errors: (this.state.email.errors || []).concat('emailInUse'),
          },
        });
      }
    }
  }

  handleAcceptTerms(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      this.setState((state: State) => ({
        acceptTerms: !state.acceptTerms,
      }));
    }
  }

  render() {
    const { isAuthing, isRegistering, handleClickLogin, signUpError, handleClose } = this.props;
    const { name, lastName, email, password, repeatPassword, acceptTerms } = this.state;
    const nameError = name.errors && FormValidator.buildErrorLabel(name.errors, {
      required: 'signup.name.error.required',
    });
    const lastNameError = lastName.errors && FormValidator.buildErrorLabel(lastName.errors, {
      required: 'signup.lastName.error.required',
    });
    const emailError = email.errors && FormValidator.buildErrorLabel(email.errors, {
      emailFormat: 'auth.email.error.format',
      required: 'auth.email.error.required',
      emailInUse: 'auth.email.error.inUse',
    });
    const passwordError = password.errors && FormValidator.buildErrorLabel(password.errors, {
      required: 'auth.password.error.required',
      min: 'auth.password.error.min',
    });
    const repeatPasswordError = repeatPassword.errors && FormValidator.buildErrorLabel(repeatPassword.errors, {
      required: 'signup.repeatPassword.error.required',
      notSame: 'signup.repeatPassword.error.noSame',
    });

    let error;
    if (signUpError) {
      if (signUpError.code === 400 && signUpError.data) {
        if (signUpError.data.name === 'UserAlreadyExistsError') {
          error = <FormattedMessage id="signup.error.userAlreadyExists">{
            txt => (<p className={classNames(style.text, style.red, style.lowerError)}>{txt}</p>)
          }</FormattedMessage>;
        }
      } else {
        error = <FormattedMessage id="signup.error.httpError">{
          txt => (<p className={classNames(style.text, style.red, style.lowerError)}>{txt}</p>)
        }</FormattedMessage>;
      }
    }

    return (
      <form className={classNames(style.authForm, formStyle.formWrap)}
            onSubmit={this.handleSubmit.bind(this)}>
        <FormattedMessage id="signup.title">{
          txt => (<h1>{txt}</h1>)
        }</FormattedMessage>
        <FormattedMessage id="signup.facebook.button">{
          txt => (<button type="button"
                          onClick={this.handleLoginFB.bind(this)}
                          disabled={isRegistering}
                          className={classNames(buttonStyle.socialBtn, buttonStyle.facebook)}>
            <img src={facebookLogo} alt="facebook"/>
            <span className={style.fbLogin}>{txt}</span>
          </button>)}</FormattedMessage>
        <div className={style.separator}>
          <hr className={style.line}/>
          <p className={style.or}>o</p>
          <hr className={style.line}/>
        </div>
        <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
          <FormattedMessage id="signup.name.label">{
            txt => (<label className={formStyle.label}>{txt}</label>)
          }</FormattedMessage>
          <input type="text"
                 className={classNames(inputStyle.input, {
                   [inputStyle.red]: name.errors && name.errors.length,
                   [inputStyle.lightBlue]: !!name.value
                 })}
                 onChange={this.handleChange.bind(this)}
                 onFocus={this.handleFocus.bind(this)}
                 onBlur={this.handleBlur.bind(this)}
                 disabled={isAuthing || isRegistering}
                 name="name"
                 value={name.value}
                 placeholder={this.context.intl.formatMessage(messages.namePlaceholder)}/>
          {nameError}
        </section>
        <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
          <FormattedMessage id="signup.lastName.label">{
            txt => (<label className={formStyle.label}>{txt}</label>)
          }</FormattedMessage>
          <input type="text"
                 className={classNames(inputStyle.input, {
                   [inputStyle.red]: lastName.errors && lastName.errors.length,
                   [inputStyle.lightBlue]: !!lastName.value
                 })}
                 onChange={this.handleChange.bind(this)}
                 onFocus={this.handleFocus.bind(this)}
                 onBlur={this.handleBlur.bind(this)}
                 disabled={isAuthing || isRegistering}
                 name="lastName"
                 value={lastName.value}
                 placeholder={this.context.intl.formatMessage(messages.lastNamePlaceholder)}/>
          {lastNameError}
        </section>
        <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
          <FormattedMessage id="auth.email.label">{
            txt => (<label className={formStyle.label}>{txt}</label>)
          }</FormattedMessage>
          <input type="email"
                 className={classNames(inputStyle.input, {
                   [inputStyle.red]: email.errors && email.errors.length,
                   [inputStyle.lightBlue]: !!email.value
                 })}
                 onChange={this.handleChange.bind(this)}
                 onFocus={this.handleFocus.bind(this)}
                 onBlur={this.handleBlur.bind(this)}
                 disabled={isAuthing || isRegistering}
                 name="email"
                 value={email.value}
                 placeholder={this.context.intl.formatMessage(messages.emailPlaceholder)}/>
          {emailError}
        </section>
        <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
          <FormattedMessage id="auth.password.label">{
            txt => (<label className={formStyle.label}>{txt}</label>)
          }</FormattedMessage>
          <input type="password"
                 className={classNames(inputStyle.input, {
                   [inputStyle.red]: password.errors && password.errors.length,
                   [inputStyle.lightBlue]: !!password.value
                 })}
                 onChange={this.handleChange.bind(this)}
                 onFocus={this.handleFocus.bind(this)}
                 onBlur={this.handleBlur.bind(this)}
                 disabled={isAuthing || isRegistering}
                 name="password"
                 value={password.value}
                 placeholder={this.context.intl.formatMessage(messages.passwordPlaceholder)}/>
          {passwordError}
        </section>
        <section className={classNames(formStyle.row, formStyle.inputRow)}>
          <FormattedMessage id="signup.repeatPassword.label">{
            txt => (<label className={formStyle.label}>{txt}</label>)
          }</FormattedMessage>
          <input type="password"
                 className={classNames(inputStyle.input, {
                   [inputStyle.red]: repeatPassword.errors && repeatPassword.errors.length,
                   [inputStyle.lightBlue]: !!repeatPassword.value
                 })}
                 onChange={this.handleChange.bind(this)}
                 onFocus={this.handleFocus.bind(this)}
                 onBlur={this.handleBlur.bind(this)}
                 disabled={isAuthing || isRegistering}
                 name="repeatPassword"
                 value={repeatPassword.value}
                 placeholder={this.context.intl.formatMessage(messages.repeatPasswordPlaceholder)}/>
          {repeatPasswordError}
        </section>
        <Checkbox handleChange={this.handleAcceptTerms.bind(this)}
                  name="acceptTerm"
                  value="acceptTerm"
                  isChecked={acceptTerms}
                  className={checkboxStyle.acceptTerms}>
          <FormattedMessage id="auth.agree">{txt => (<span>{txt} </span>)}</FormattedMessage>
          <FormattedMessage id="auth.terms">{txt => (
            <Link to="/terms-of-service"
                  onClick={() => !!handleClose && handleClose()}>{txt}</Link>)}</FormattedMessage>
          <FormattedMessage id="auth.and">{txt => <span> {txt} </span>}</FormattedMessage>
          <FormattedMessage id="auth.policy">{txt => (
            <Link to="/privacy-policy"
                  onClick={() => !!handleClose && handleClose()}>{txt}</Link>)}</FormattedMessage>
          .
        </Checkbox>
        {error}
        <FormattedMessage id="signup.button">{
          txt => (<button type="submit"
                          disabled={isAuthing || isRegistering || !acceptTerms}
                          className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.submitBtn)}
                          onClick={this.handleSubmit.bind(this)}>{txt}
          </button>)
        }</FormattedMessage>

        <FormattedMessage id="auth.notLogged">{
          txt => (
            <p className={classNames(style.text, style.separated)}>{txt} <FormattedMessage id="auth.loginHere">{
              t => <Link onClick={handleClickLogin} to="/login">{t}</Link>
            }</FormattedMessage>.</p>)
        }</FormattedMessage>
      </form>

    );
  }
}

SignUpForm.propTypes = {
  fbLogin: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  isAuthing: PropTypes.bool,
  isRegistering: PropTypes.bool,
  signUpError: PropTypes.object,
  authenticated: PropTypes.bool,
  redirectTo: PropTypes.string,
  handleClickLogin: PropTypes.func,
  authModalVisible: PropTypes.bool,
  authModalShowLogin: PropTypes.bool,
  handleClose: PropTypes.func,
  locale: PropTypes.object,
  registerSuccessState: PropTypes.bool,
};

SignUpForm.contextTypes = {
  intl: intlShape
};



// WEBPACK FOOTER //
// ./src/modules/auth/components/SignUp/SignUpForm/SignUpForm.jsx